/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { Box, ButtonBase, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import type { FlowRunProgress, FlowRunStatus } from '@poweralpha/common';
import { useState } from 'react';
import { getFailureSummary } from './progress';
import { getRunRowDisplay, type RunGlyph } from './runRowDisplay';
import { getGlyphSx, getTrackSx, tabularSx } from './simProgressStyles';

const GLYPH_ICON: Record<RunGlyph, React.ReactNode> = {
  tasks: <BoltRoundedIcon color="secondary" />,
  cases: <LayersOutlinedIcon color="primary" />,
  queued: <SyncRoundedIcon color="secondary" />,
  done: <CheckCircleOutlineRoundedIcon color="success" />,
  failed: <ErrorOutlineRoundedIcon color="error" />,
  cancelled: <DoNotDisturbOnOutlinedIcon color="action" />,
};

const ACTION_COLOR: Record<RunGlyph, string> = {
  tasks: 'text.primary',
  cases: 'text.primary',
  queued: 'text.primary',
  done: 'primary.main',
  failed: 'error.main',
  cancelled: 'text.secondary',
};

interface Props {
  run: FlowRunStatus;
  progress: FlowRunProgress | undefined;
  fraction: number | null;
  canOpen: boolean;
  onOpen: (run: FlowRunStatus) => void;
}

/** One flow run in the simulations popover: status glyph, progress bar and counts. */
export function SimRunRow({ run, progress, fraction, canOpen, onOpen }: Readonly<Props>) {
  const [showDetails, setShowDetails] = useState(false);
  const { glyph, tone, value, action, meta, timing } = getRunRowDisplay(
    run,
    progress,
    fraction,
    canOpen,
  );
  const isFailed = glyph === 'failed';
  const isClickable = isFailed || canOpen;

  return (
    <ButtonBase
      component="div"
      disabled={!isClickable}
      onClick={() => (isFailed ? setShowDetails((prev) => !prev) : onOpen(run))}
      aria-expanded={isFailed ? showDetails : undefined}
      data-testid="sim-run-row"
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        borderRadius: '10px',
        px: 1.5,
        py: 1.25,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
        <Box sx={getGlyphSx}>{GLYPH_ICON[glyph]}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
            {run.flow_name}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11, color: 'text.disabled' }}>
            {run.name}
          </Typography>
        </Box>
        {action && (
          <Typography
            sx={{ ...tabularSx, fontSize: 12, fontWeight: 600, color: ACTION_COLOR[glyph] }}
          >
            {action}
          </Typography>
        )}
      </Stack>
      <LinearProgress
        variant={value === null ? 'indeterminate' : 'determinate'}
        value={value === null ? undefined : value * 100}
        aria-label={`${run.flow_name} progress`}
        sx={(theme) => ({ ...getTrackSx(theme, tone), mt: 1 })}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 0.75, justifyContent: 'space-between' }}>
        <Typography noWrap sx={{ ...tabularSx, fontSize: 11, color: 'text.secondary' }}>
          {meta}
        </Typography>
        <Typography noWrap sx={{ ...tabularSx, fontSize: 11, color: 'text.disabled' }}>
          {timing}
        </Typography>
      </Stack>
      {isFailed && (
        <Collapse in={showDetails} unmountOnExit>
          <Typography
            sx={{
              mt: 1,
              p: 1,
              fontSize: 11,
              borderRadius: '8px',
              color: 'text.secondary',
              backgroundColor: 'action.hover',
              wordBreak: 'break-word',
            }}
          >
            {getFailureSummary(run)}
          </Typography>
        </Collapse>
      )}
    </ButtonBase>
  );
}
